import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

const IdeaSelector = ({ selectedId, onSelectIdea, placeholder = "Select an idea..." }) => {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchIdeas();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchIdeas = async () => {
    try {
      const response = await fetch('/api/ideas/get_ideas?limit=1000&offset=0');
      if (response.ok) {
        const result = await response.json();
        setIdeas(result.ideas || []);
      }
    } catch (error) {
      console.error('Error fetching ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredIdeas = ideas.filter(idea => {
    const searchLower = searchTerm.toLowerCase();
    const title = idea['Title & Sponsor'] || '';
    const id = idea.Id?.toString() || '';
    const status = idea.Status || '';
    return (
      title.toLowerCase().includes(searchLower) ||
      id.includes(searchLower) ||
      status.toLowerCase().includes(searchLower)
    );
  });

  const selectedIdea = ideas.find(idea => idea.Id === selectedId);
  const selectedTitle = selectedIdea ? selectedIdea['Title & Sponsor'] : null;

  const handleSelect = (idea) => {
    onSelectIdea(idea.Id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelectIdea(null);
    setSearchTerm('');
  };

  const getStatusColor = (status) => {
    const colors = {
      'Approved': 'bg-green-100 text-green-800',
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Under Review': 'bg-blue-100 text-blue-800',
      'In Review': 'bg-blue-100 text-blue-800',
      'Rejected': 'bg-red-100 text-red-800',
      'ITPM Review': 'bg-orange-100 text-orange-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%', maxWidth: '500px' }}>
      {/* Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: 'var(--cares-surface-card)',
          border: '2px solid var(--cares-line-card)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          transition: 'all 0.2s',
          fontSize: '14px',
          fontWeight: '500',
          color: selectedTitle ? 'var(--cares-text-1)' : 'var(--cares-text-label)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--cares-blue)';
          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--cares-line-card)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <span style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          flex: 1,
          textAlign: 'left'
        }}>
          {selectedTitle || placeholder}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
          {selectedId && (
            <X 
              size={16} 
              onClick={handleClear}
              style={{ color: 'var(--cares-text-label)', cursor: 'pointer' }}
            />
          )}
          <ChevronDown 
            size={18} 
            style={{ 
              color: 'var(--cares-text-label)',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }} 
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          right: 0,
          background: 'var(--cares-surface-card)',
          border: '1px solid var(--cares-line-card)',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          maxHeight: '400px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Search Input */}
          <div style={{ padding: '12px', borderBottom: '1px solid var(--cares-line-card)' }}>
            <div style={{ position: 'relative' }}>
              <Search
                size={18}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--cares-text-note)'
                }}
              />
              <input
                type="text"
                placeholder="Search ideas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 40px',
                  border: '1px solid var(--cares-line-card)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                autoFocus
              />
            </div>
          </div>

          {/* Ideas List */}
          <div style={{
            overflowY: 'auto',
            maxHeight: '320px',
            padding: '8px'
          }}>
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--cares-text-label)' }}>
                Loading ideas...
              </div>
            ) : filteredIdeas.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--cares-text-label)' }}>
                No ideas found
              </div>
            ) : (
              filteredIdeas.map((idea) => (
                <button
                  key={idea.Id}
                  onClick={() => handleSelect(idea)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: selectedId === idea.Id ? 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))' : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.2s',
                    marginBottom: '4px'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedId !== idea.Id) {
                      e.currentTarget.style.background = 'var(--cares-surface-board)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedId !== idea.Id) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px'
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'var(--cares-text-1)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginBottom: '4px'
                      }}>
                        {idea['Title & Sponsor']}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: 'var(--cares-text-label)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span>ID: {idea.Id}</span>
                        {idea['Submitted By'] && (
                          <>
                            <span>•</span>
                            <span>{idea['Submitted By']}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {idea.Status && (
                      <span
                        className={getStatusColor(idea.Status)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {idea.Status}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IdeaSelector;

